/**
 * @jsxRuntime classic
 */
import React, { type ComponentType, forwardRef } from 'react';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import HelpIcon from '@atlaskit/icon/core/question-circle';

import { BadgeContainer } from '../../components/badge-container';
import { ListItem } from '../../components/list-item';

import { IconButton } from './themed/migration';

const HELP_NOTIFICATION_BADGE_ID = 'atlassian-navigation-help-notification-count';

type HelpProps = {
	/**
	 * The component to render as the badge.
	 * You are recommended to use the Badge component from `@atlaskit/badge`.
	 */
	badge?: ComponentType;
	/**
	 * Indicates that the button is selected.
	 */
	isSelected?: boolean;
	/**
	 * Provide an accessible label, often used by screen readers.
	 */
	label: React.ReactNode;
	/**
	 * Handler called on click. Expected to open the help panel.
	 *
	 * You can use the second argument to fire Atlaskit analytics events on custom channels.
	 * They could then be routed to GASv3 analytics. See the pressable or anchor primitive code examples for
	 * information on [firing Atlaskit analytics events](https://atlassian.design/components/primitives/pressable/examples#atlaskit-analytics)
	 * or [routing these to GASv3 analytics](https://atlassian.design/components/primitives/pressable/examples#gasv3-analytics).
	 */
	onClick?: (e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
	/**
	 * A unique string that appears as data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
	/**
	 * An optional name used to identify events for [React UFO (Unified Frontend Observability) press interactions](https://developer.atlassian.com/platform/ufo/react-ufo/react-ufo/getting-started/#quick-start--press-interactions). For more information, see [React UFO integration into Design System components](https://go.atlassian.com/react-ufo-dst-integration).
	 */
	interactionName?: string;
};

/**
 * __Help__
 *
 * The trigger button for the help menu in the top navigation bar.
 */
export const Help = forwardRef<HTMLButtonElement, HelpProps>(
	({ badge, onClick, label, isSelected, testId, interactionName }: HelpProps, forwardedRef) => {
		const button = (
			<IconButton
				icon={HelpIcon}
				label={label}
				onClick={onClick}
				appearance="subtle"
				isSelected={isSelected}
				ref={forwardedRef}
				isTooltipDisabled={false}
				testId={testId}
				interactionName={interactionName}
			/>
		);

		if (!badge) {
			return <ListItem>{button}</ListItem>;
		}

		return (
			<BadgeContainer id={HELP_NOTIFICATION_BADGE_ID} badge={badge} role="listitem">
				{button}
			</BadgeContainer>
		);
	},
);
